import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { Download } from "lucide-react";

import { resolveDocumentListSearch } from "@integrator-src/api/document/document-search-query";
import { isPersonOwnedWipDocument } from "@integrator-src/api/document/document-owner-label";
import { apiFetch, apiUploadFormData, ApiError } from "@/lib/api";
import {
  controlledDocumentIsDownloadable,
  controlledDocumentListActiveRevisionLabel,
  controlledDocumentListStatusSummary,
  documentKindWorkbenchFieldOptions,
  documentKindWorkbenchFilterOptions,
  documentSharesRevisionLineage,
  downloadControlledDocument,
  fetchDocumentLookups,
  filenameExtension,
  formatEcoNumber,
  formatTimestamp,
  groupControlledDocumentsForList,
  nextRevisionLetter,
  type ControlledDocument,
  type DocumentNote,
  type DocumentRevisionEvent,
  type DocumentInboundEmailThread,
  type DocumentSkuOption,
  type ProductCollectionRelation,
  type DocumentCollectionSkusResponse,
  type ProductTransitionNotice,
} from "@/lib/documents";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  CardTitleWithHelp,
  HoverHintContent,
  HoverHintTrigger,
  ReadOnlyHoverLayer,
} from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { SkuSearchableSelect } from "@/components/mrp/SkuSearchableSelect";
import { fetchPreviewBlob, previewQueryKey } from "./DocumentFilePreview";
import { DocumentModal } from "./DocumentModal";
import { ControlledDocumentDetailModal } from "./ControlledDocumentDetailModal";
import {
  documentInboundEmailThreadHoverHint,
  isInboundEmailCreatedEvent,
  type DocumentDetailPreviewMode,
} from "./DocumentDetailPreviewPane";
import {
  controlledDocumentTargetsAsPills,
  primarySkuFromControlledDocument,
} from "./controlledDocumentTargets";

const DOCUMENTS_QUERY_KEY = ["documents"] as const;
const LOOKUPS_QUERY_KEY = ["documents", "lookups"] as const;
const DOCUMENT_SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [value, delayMs]);
  return debounced;
}

type WorkbenchSelection =
  | { type: "all" }
  | { type: "wip" }
  | { type: "collection"; id: string }
  | { type: "sku"; sku: string; collectionId?: string };

type UploadFileStatus = "pending" | "uploading" | "uploaded" | "failed";

interface UploadStatus {
  name: string;
  status: UploadFileStatus;
}

type DocumentSortKey =
  | "updated_at"
  | "title"
  | "document_kind_label"
  | "extension"
  | "revision"
  | "lifecycle_status";

type SortDirection = "asc" | "desc";

function lifecycleRank(status: ControlledDocument["lifecycle_status"]): number {
  if (status === "draft") return 0;
  if (status === "wip") return 1;
  if (status === "current") return 2;
  return 3;
}

function compareDocuments(
  documentA: ControlledDocument,
  documentB: ControlledDocument,
  key: DocumentSortKey,
  dir: SortDirection,
): number {
  const factor = dir === "asc" ? 1 : -1;
  if (key === "updated_at") {
    return factor * (new Date(documentA.updated_at).getTime() - new Date(documentB.updated_at).getTime());
  }
  if (key === "title") {
    const primary = factor * documentA.title.localeCompare(documentB.title, undefined, { sensitivity: "base" });
    if (primary !== 0) return primary;
    return documentA.document_kind_label.localeCompare(documentB.document_kind_label, undefined, {
      sensitivity: "base",
    });
  }
  if (key === "document_kind_label") {
    const primary =
      factor *
      documentA.document_kind_label.localeCompare(documentB.document_kind_label, undefined, {
        sensitivity: "base",
      });
    if (primary !== 0) return primary;
    return documentA.title.localeCompare(documentB.title, undefined, { sensitivity: "base" });
  }
  if (key === "extension") {
    const extensionA = filenameExtension(documentA.original_filename) ?? "";
    const extensionB = filenameExtension(documentB.original_filename) ?? "";
    return factor * extensionA.localeCompare(extensionB, undefined, { sensitivity: "base" });
  }
  if (key === "revision") {
    return factor * documentA.revision.localeCompare(documentB.revision, undefined, { sensitivity: "base" });
  }
  return factor * (lifecycleRank(documentA.lifecycle_status) - lifecycleRank(documentB.lifecycle_status));
}

function compareDocumentGroups(
  groupA: { revisions: ControlledDocument[]; representative: ControlledDocument },
  groupB: { revisions: ControlledDocument[]; representative: ControlledDocument },
  key: DocumentSortKey,
  dir: SortDirection,
): number {
  if (key === "revision") {
    const factor = dir === "asc" ? 1 : -1;
    const labelA = controlledDocumentListActiveRevisionLabel(groupA.revisions);
    const labelB = controlledDocumentListActiveRevisionLabel(groupB.revisions);
    if (labelA === "—" && labelB !== "—") return factor;
    if (labelB === "—" && labelA !== "—") return -factor;
    return factor * labelA.localeCompare(labelB, undefined, { sensitivity: "base" });
  }
  return compareDocuments(groupA.representative, groupB.representative, key, dir);
}

function statusClass(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "current") return "bg-green-50 text-green-700";
  if (status === "wip") return "bg-amber-50 text-amber-800";
  if (status === "draft") return "bg-blue-50 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

function statusChipLabel(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "wip") return "WIP";
  return status;
}

function revisionEventActorLabel(event: DocumentRevisionEvent): string {
  const trimmed = event.created_by_display_name?.trim();
  if (trimmed) return trimmed;
  return event.created_by_email ?? "Unknown";
}

function revisionChangeNotesHoverHint(revision: ControlledDocument): string {
  const notes = revision.change_notes?.trim();
  if (notes) return notes;
  return "No revision change notes for this revision yet.";
}

function transitionStatusClass(status: ProductTransitionNotice["status"]): string {
  if (status === "issued") return "bg-blue-50 text-blue-700";
  if (status === "completed") return "bg-green-50 text-green-700";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-700";
}

function documentMatchesSelection(document: ControlledDocument, selection: WorkbenchSelection): boolean {
  if (selection.type === "all") return true;
  if (selection.type === "wip") return isPersonOwnedWipDocument(document);
  if (selection.type === "collection") {
    return document.targets?.some(
      (target) => target.target_type === "collection" && target.product_collection_id === selection.id,
    );
  }
  return document.targets?.some((target) => target.target_type === "sku" && target.sku === selection.sku);
}

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const hubWritesAllowed = useHubModuleWrite("documents");
  const documentsReadOnlyTitle = "Your role has read-only access to the Document Control hub module.";
  const [selection, setSelection] = useState<WorkbenchSelection>({ type: "all" });
  const [targetType, setTargetType] = useState<"sku" | "collection">("collection");
  const [skuQuery, setSkuQuery] = useState("");
  const [selectedSku, setSelectedSku] = useState("");
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [documentKindCode, setDocumentKindCode] = useState("");
  const [revision, setRevision] = useState("A");
  const [changeOrderId, setChangeOrderId] = useState("");
  const [changeNotes, setChangeNotes] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [documentKindFilter, setDocumentKindFilter] = useState("");
  const [documentKindUploadSearch, setDocumentKindUploadSearch] = useState("");
  const [documentKindFilterSearch, setDocumentKindFilterSearch] = useState("");
  const [editDocumentKindSearch, setEditDocumentKindSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPreset, setUploadPreset] = useState<"full" | "draft_revision">("full");
  const [draftRevisionSource, setDraftRevisionSource] = useState<ControlledDocument | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<ControlledDocument | null>(null);
  const [detailPreviewMode, setDetailPreviewMode] = useState<DocumentDetailPreviewMode>("file");
  const [documentSort, setDocumentSort] = useState<{ key: DocumentSortKey; dir: SortDirection }>({
    key: "document_kind_label",
    dir: "asc",
  });
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDocumentKindCode, setEditDocumentKindCode] = useState("");
  const [editRevision, setEditRevision] = useState("");
  const [revisionContextMenu, setRevisionContextMenu] = useState<{
    revision: ControlledDocument;
    clientX: number;
    clientY: number;
  } | null>(null);
  const [revisionChangeNotesEdit, setRevisionChangeNotesEdit] = useState<ControlledDocument | null>(null);
  const [revisionChangeNotesDraft, setRevisionChangeNotesDraft] = useState("");
  const [uploadValidationError, setUploadValidationError] = useState<string | null>(null);

  useEffect(() => {
    const sku = searchParams.get("sku")?.trim();
    const uploadParam = searchParams.get("upload");
    const shouldOpenUpload = uploadParam === "1" || uploadParam === "true";
    if (!sku || !shouldOpenUpload) return;

    setSelection({ type: "sku", sku });
    setTargetType("sku");
    setSelectedSku(sku);
    setSkuQuery(sku);
    setDocumentKindUploadSearch("");
    setUploadPreset("full");
    setDraftRevisionSource(null);
    setUploadOpen(true);

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("sku");
        next.delete("upload");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const debouncedSearchText = useDebouncedValue(searchText.trim(), DOCUMENT_SEARCH_DEBOUNCE_MS);
  const documentListSearchQuery = useMemo(() => {
    if (!resolveDocumentListSearch(debouncedSearchText, undefined)) return undefined;
    return debouncedSearchText;
  }, [debouncedSearchText]);
  const documentSearchPending = searchText.trim() !== debouncedSearchText;

  const lookupsQuery = useQuery({
    queryKey: LOOKUPS_QUERY_KEY,
    queryFn: fetchDocumentLookups,
  });

  const documentsQuery = useQuery<{ documents: ControlledDocument[] }, ApiError>({
    queryKey: [...DOCUMENTS_QUERY_KEY, statusFilter, documentKindFilter, documentListSearchQuery],
    queryFn: () =>
      apiFetch<{ documents: ControlledDocument[] }>("/api/documents", {
        query: {
          status: statusFilter || undefined,
          documentKindCode: documentKindFilter || undefined,
          q: documentListSearchQuery || undefined,
        },
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const documentId = searchParams.get("document")?.trim();
    if (!documentId) return;
    let cancelled = false;

    void (async () => {
      try {
        const result = await apiFetch<{ document: ControlledDocument }>(
          `/api/documents/${encodeURIComponent(documentId)}`,
        );
        if (cancelled) return;
        const found = result.document;
        const collectionTarget = found.targets?.find(
          (target) => target.target_type === "collection" && target.product_collection_id,
        );
        const skuTarget = found.targets?.find((target) => target.target_type === "sku" && target.sku);
        if (collectionTarget?.product_collection_id) {
          setSelection({ type: "collection", id: collectionTarget.product_collection_id });
        } else if (skuTarget?.sku) {
          setSelection({ type: "sku", sku: skuTarget.sku });
          setTargetType("sku");
          setSelectedSku(skuTarget.sku);
          setSkuQuery(skuTarget.sku);
        } else if (isPersonOwnedWipDocument(found)) {
          setSelection({ type: "wip" });
        } else {
          setSelection({ type: "all" });
        }
        setStatusFilter("");
        setSelectedDocument(found);
      } catch {
        if (cancelled) return;
      } finally {
        if (cancelled) return;
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.delete("document");
            return next;
          },
          { replace: true },
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  const selectedCollectionIdForQueries =
    selection.type === "collection" ? selection.id : selection.type === "sku" ? selection.collectionId : undefined;

  const collectionSkusQuery = useQuery<DocumentCollectionSkusResponse>({
    queryKey: ["document-collections", selectedCollectionIdForQueries, "skus"],
    queryFn: () =>
      apiFetch<DocumentCollectionSkusResponse>(
        `/api/document-collections/${selectedCollectionIdForQueries ?? ""}/skus`,
      ),
    enabled: selectedCollectionIdForQueries != null,
  });

  const relationshipsQuery = useQuery<{ relationships: ProductCollectionRelation[] }>({
    queryKey: ["document-collections", selectedCollectionIdForQueries, "relationships"],
    queryFn: () =>
      apiFetch<{ relationships: ProductCollectionRelation[] }>(
        `/api/document-collections/${selectedCollectionIdForQueries ?? ""}/relationships`,
      ),
    enabled: selectedCollectionIdForQueries != null,
  });

  const transitionNoticesQuery = useQuery<{ transitionNotices: ProductTransitionNotice[] }>({
    queryKey: ["document-transition-notices", selectedCollectionIdForQueries, "workbench"],
    queryFn: () =>
      apiFetch<{ transitionNotices: ProductTransitionNotice[] }>("/api/document-transition-notices", {
        query: {
          productCollectionId: selectedCollectionIdForQueries,
        },
      }),
    enabled: selectedCollectionIdForQueries != null,
  });

  const notesQuery = useQuery<{ notes: DocumentNote[] }>({
    queryKey: ["document-notes", selection],
    queryFn: () =>
      apiFetch<{ notes: DocumentNote[] }>("/api/document-notes", {
        query: {
          sku: selection.type === "sku" ? selection.sku : undefined,
          productCollectionId: selection.type === "collection" ? selection.id : undefined,
        },
      }),
    enabled: selection.type === "sku" || selection.type === "collection",
  });

  const skuQueryResult = useQuery<{ skus: DocumentSkuOption[] }>({
    queryKey: ["documents", "sku-search", skuQuery],
    queryFn: () => apiFetch<{ skus: DocumentSkuOption[] }>("/api/document-skus", { query: { q: skuQuery } }),
    enabled: targetType === "sku",
  });

  const documentHistoryQuery = useQuery<{
    revisions: ControlledDocument[];
    events: DocumentRevisionEvent[];
    inboundEmailThread: DocumentInboundEmailThread | null;
  }>({
    queryKey: ["documents", "history", selectedDocument?.id],
    queryFn: () =>
      apiFetch<{
        revisions: ControlledDocument[];
        events: DocumentRevisionEvent[];
        inboundEmailThread: DocumentInboundEmailThread | null;
      }>(`/api/documents/${selectedDocument?.id}/history`),
    enabled: selectedDocument != null,
  });

  useEffect(() => {
    setDetailPreviewMode("file");
  }, [selectedDocument?.id]);

  const activeChangeOrders = useMemo(
    () =>
      (lookupsQuery.data?.changeOrders ?? []).filter(
        (changeOrder) => changeOrder.status !== "implemented" && changeOrder.status !== "cancelled",
      ),
    [lookupsQuery.data?.changeOrders],
  );

  const documentKindFieldOptions = useMemo(
    () => documentKindWorkbenchFieldOptions(lookupsQuery.data?.kinds ?? []),
    [lookupsQuery.data?.kinds],
  );

  const documentKindFilterSelectOptions = useMemo(
    () => documentKindWorkbenchFilterOptions(lookupsQuery.data?.kinds ?? []),
    [lookupsQuery.data?.kinds],
  );

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (files.length === 0) throw new Error("Choose at least one file to upload.");
      if (targetType === "sku" && selectedSku.trim() === "") throw new Error("Choose a SKU.");
      if (targetType === "collection" && selectedCollectionId === "") throw new Error("Choose a product collection.");
      const uploaded: ControlledDocument[] = [];
      setUploadStatuses(files.map((file) => ({ name: file.name, status: "pending" })));
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploadStatuses((current) =>
          current.map((item, itemIndex) => (itemIndex === index ? { ...item, status: "uploading" } : item)),
        );
        const formData = new FormData();
        formData.append("file", file);
        formData.append("targetType", targetType);
        if (targetType === "sku") formData.append("sku", selectedSku.trim());
        if (targetType === "collection") formData.append("productCollectionId", selectedCollectionId);
        if (files.length === 1 && documentKindCode.trim() !== "") {
          formData.append("documentKindCode", documentKindCode.trim());
        }
        formData.append("revision", revision.trim() || "A");
        if (files.length === 1 && title.trim()) formData.append("title", title.trim());
        if (description.trim()) formData.append("description", description.trim());
        if (changeOrderId && files.length === 1) formData.append("changeOrderId", changeOrderId);
        if (changeNotes.trim()) formData.append("changeNotes", changeNotes.trim());
        try {
          const result = await apiUploadFormData<{ document: ControlledDocument }>("/api/documents", formData);
          uploaded.push(result.document);
          setUploadStatuses((current) =>
            current.map((item, itemIndex) => (itemIndex === index ? { ...item, status: "uploaded" } : item)),
          );
        } catch (error) {
          setUploadStatuses((current) =>
            current.map((item, itemIndex) => (itemIndex === index ? { ...item, status: "failed" } : item)),
          );
          throw error;
        }
      }
      return { documents: uploaded };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      setFiles([]);
      setTitle("");
      setDescription("");
      setRevision("A");
      setChangeNotes("");
      setUploadStatuses([]);
      setUploadOpen(false);
      setUploadPreset("full");
      setDraftRevisionSource(null);
      setUploadValidationError(null);
      setDocumentKindCode("");
      setDocumentKindUploadSearch("");
      const input = document.querySelector<HTMLInputElement>('input[data-documents-file-input="true"]');
      if (input) input.value = "";
    },
    onError: (error) => {
      setUploadValidationError(error instanceof Error ? error.message : "Upload failed.");
    },
  });

  function handleUploadClick(): void {
    if (!hubWritesAllowed) return;
    if (files.length === 0) {
      setUploadValidationError("Choose at least one file to upload.");
      return;
    }
    if (targetType === "sku" && selectedSku.trim() === "") {
      setUploadValidationError("Choose a part number / SKU.");
      return;
    }
    if (targetType === "collection" && selectedCollectionId === "") {
      setUploadValidationError("Choose a product collection.");
      return;
    }
    setUploadValidationError(null);
    uploadMutation.mutate();
  }

  const updateDocumentMutation = useMutation({
    mutationFn: () => {
      if (!selectedDocument) throw new Error("Choose a document to update.");
      return apiFetch<{ document: ControlledDocument }>(`/api/documents/${selectedDocument.id}`, {
        method: "PATCH",
        body: {
          title: editTitle.trim(),
          description: editDescription.trim() || null,
          documentKindCode: editDocumentKindCode.trim() === "" ? null : editDocumentKindCode.trim(),
          revision: editRevision.trim(),
        },
      });
    },
    onSuccess: ({ document }) => {
      setSelectedDocument(document);
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["documents", "history", document.id] });
    },
  });

  const patchRevisionChangeNotesMutation = useMutation({
    mutationFn: ({ documentId, changeNotes }: { documentId: string; changeNotes: string }) =>
      apiFetch<{ document: ControlledDocument }>(`/api/documents/${documentId}`, {
        method: "PATCH",
        body: { changeNotes: changeNotes.trim() || null },
      }),
    onSuccess: ({ document }) => {
      setRevisionChangeNotesEdit(null);
      setRevisionContextMenu(null);
      if (selectedDocument?.id === document.id) {
        setSelectedDocument(document);
      }
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      if (selectedDocument) {
        void queryClient.invalidateQueries({ queryKey: ["documents", "history", selectedDocument.id] });
      }
    },
  });

  const deleteRevisionMutation = useMutation({
    mutationFn: (documentId: string) =>
      apiFetch<void>(`/api/documents/${encodeURIComponent(documentId)}`, { method: "DELETE" }),
    onSuccess: (_data, deletedId) => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      if (selectedDocument?.id === deletedId) {
        setSelectedDocument(null);
      } else if (selectedDocument) {
        void queryClient.invalidateQueries({ queryKey: ["documents", "history", selectedDocument.id] });
      }
    },
  });

  function requestDeleteRevision(document: ControlledDocument): void {
    if (document.lifecycle_status === "current") return;
    if (
      !window.confirm(
        `Delete ${document.title} rev ${document.revision}? This removes the stored file and cannot be undone.`,
      )
    ) {
      return;
    }
    deleteRevisionMutation.mutate(document.id);
  }

  const releaseWipMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDocument) throw new Error("Choose a document.");
      await apiFetch<{ document: ControlledDocument }>(`/api/documents/${selectedDocument.id}`, {
        method: "PATCH",
        body: {
          title: editTitle.trim(),
          description: editDescription.trim() || null,
          documentKindCode: editDocumentKindCode.trim() === "" ? null : editDocumentKindCode.trim(),
          revision: editRevision.trim(),
        },
      });
      return apiFetch<{ document: ControlledDocument }>(`/api/documents/${selectedDocument.id}/release-wip`, {
        method: "POST",
      });
    },
    onSuccess: ({ document }) => {
      setSelectedDocument(document);
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["documents", "history", document.id] });
    },
  });

  /**
   * Prefetch for the detail modal (preview blob + revision history).
   * Disabled: cold /history is ~180–200ms and fine; unconstrained hover prefetch
   * stampeded the API DB pool (many concurrent /history holders).
   *
   * TODO(documents-prefetch): re-enable with limits — e.g. dwell time before
   * prefetch (skip mouse-through), reuse cache on second hover of the same id,
   * and at most one in-flight prefetch at a time.
   */
  function prefetchDocumentPreview(document: ControlledDocument): void {
    const documentsHoverPrefetchEnabled = false;
    if (!documentsHoverPrefetchEnabled) {
      return;
    }
    void queryClient.prefetchQuery({
      queryKey: previewQueryKey(document.id),
      queryFn: ({ signal }) => fetchPreviewBlob(document.id, signal),
      staleTime: 240_000,
      gcTime: 240_000,
    });
    void queryClient.prefetchQuery({
      queryKey: ["documents", "history", document.id],
      queryFn: () =>
        apiFetch<{
          revisions: ControlledDocument[];
          events: DocumentRevisionEvent[];
          inboundEmailThread: DocumentInboundEmailThread | null;
        }>(`/api/documents/${document.id}/history`),
      staleTime: 60_000,
    });
  }

  function openDocumentDetails(document: ControlledDocument): void {
    setDetailPreviewMode("file");
    setSelectedDocument(document);
    setEditTitle(document.title);
    setEditDescription(document.description ?? "");
    setEditDocumentKindCode(document.document_kind_code ?? "");
    setEditDocumentKindSearch("");
    setEditRevision(document.revision);
  }

  function openRevisionChangeNotesEditor(revision: ControlledDocument): void {
    setRevisionContextMenu(null);
    setRevisionChangeNotesEdit(revision);
    setRevisionChangeNotesDraft(revision.change_notes ?? "");
  }

  function openUploadForSelection(): void {
    if (selection.type === "collection") {
      setTargetType("collection");
      setSelectedCollectionId(selection.id);
    }
    if (selection.type === "sku") {
      setTargetType("sku");
      setSelectedSku(selection.sku);
      setSkuQuery(selection.sku);
    }
    setDocumentKindUploadSearch("");
    setUploadPreset("full");
    setDraftRevisionSource(null);
    setUploadOpen(true);
  }

  function openUploadFromControlledDocument(
    controlledDocument: ControlledDocument,
    preset: "replacement" | "draft_revision",
  ): void {
    const targets = controlledDocument.targets ?? [];
    const collectionTarget = targets.find((target) => target.target_type === "collection");
    const skuTarget = targets.find((target) => target.target_type === "sku");
    if (collectionTarget?.product_collection_id) {
      setTargetType("collection");
      setSelectedCollectionId(collectionTarget.product_collection_id);
      setSelection({ type: "collection", id: collectionTarget.product_collection_id });
    } else if (skuTarget?.sku) {
      setTargetType("sku");
      setSelectedSku(skuTarget.sku);
      setSkuQuery(skuTarget.sku);
      setSelection({ type: "sku", sku: skuTarget.sku });
    }
    if (preset === "replacement") {
      setUploadPreset("full");
      setDraftRevisionSource(null);
      setChangeOrderId(activeChangeOrders.length > 0 ? activeChangeOrders[0].id : "");
      setTitle("");
    } else {
      setUploadPreset("draft_revision");
      setDraftRevisionSource(controlledDocument);
      setChangeOrderId("");
      setTitle(controlledDocument.title);
      setDescription(controlledDocument.description ?? "");
      setChangeNotes("");
    }
    setDocumentKindCode(controlledDocument.document_kind_code ?? "");
    const historyRows =
      controlledDocument.id === selectedDocument?.id ? documentHistoryQuery.data?.revisions : undefined;
    const siblingRows =
      historyRows && historyRows.length > 0
        ? historyRows
        : (documentsQuery.data?.documents ?? []).filter((document) =>
            documentSharesRevisionLineage(document, controlledDocument),
          );
    setRevision(nextRevisionLetter(siblingRows.map((row) => row.revision)));
    setFiles([]);
    setUploadStatuses([]);
    const input = window.document.querySelector<HTMLInputElement>('input[data-documents-file-input="true"]');
    if (input) input.value = "";
    setSelectedDocument(null);
    setDocumentKindUploadSearch("");
    setUploadOpen(true);
  }

  const collections = lookupsQuery.data?.collections ?? [];
  const selectedCollection =
    selectedCollectionIdForQueries != null
      ? collections.find((collection) => collection.id === selectedCollectionIdForQueries) ?? null
      : null;
  const skuOptions = useMemo(
    () =>
      (skuQueryResult.data?.skus ?? []).map((sku) => ({
        value: sku.sku,
        label: `${sku.sku}${sku.product_name ? ` - ${sku.product_name}` : ""}`,
      })),
    [skuQueryResult.data?.skus],
  );
  const documents = documentsQuery.data?.documents ?? [];
  const selectedDocuments = documents.filter((document) => documentMatchesSelection(document, selection));
  const documentGroups = useMemo(
    () => groupControlledDocumentsForList(selectedDocuments),
    [selectedDocuments],
  );
  const sortedDocumentGroups = useMemo(
    () =>
      [...documentGroups].sort((groupA, groupB) =>
        compareDocumentGroups(groupA, groupB, documentSort.key, documentSort.dir),
      ),
    [documentGroups, documentSort],
  );

  function toggleDocumentSort(key: DocumentSortKey): void {
    setDocumentSort((previous) => {
      if (previous.key === key) {
        return { key, dir: previous.dir === "asc" ? "desc" : "asc" };
      }
      const defaultDir = key === "updated_at" ? "desc" : "asc";
      return { key, dir: defaultDir };
    });
  }
  const openChangeOrders = activeChangeOrders.filter((changeOrder) =>
    selectedDocuments.some((document) => document.change_order_id === changeOrder.id),
  );
  const activeTransitionNotices = (transitionNoticesQuery.data?.transitionNotices ?? []).filter(
    (notice) => notice.status === "draft" || notice.status === "issued",
  );
  const workbenchBusy =
    updateDocumentMutation.isPending ||
    releaseWipMutation.isPending ||
    deleteRevisionMutation.isPending ||
    documentsQuery.isFetching ||
    documentSearchPending;
  const uploadedCount = uploadStatuses.filter((item) => item.status === "uploaded").length;
  const failedCount = uploadStatuses.filter((item) => item.status === "failed").length;
  const uploadTotal = uploadStatuses.length;
  const uploadPercent = uploadTotal > 0 ? Math.round((uploadedCount / uploadTotal) * 100) : 0;
  const currentUploadOrdinal = uploadMutation.isPending ? Math.min(uploadedCount + 1, uploadTotal) : uploadedCount;
  const isDraftRevisionUpload = uploadPreset === "draft_revision" && draftRevisionSource != null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Document Control Workbench</h1>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                variant="secondary"
                disabled={!hubWritesAllowed}
                onClick={() => openUploadForSelection()}
              >
                Upload initial revision
              </Button>
            }
          >
            Upload a new controlled document for the current scope (new title or first revision).
          </HoverHintTrigger>
          </ReadOnlyHoverLayer>
          <Link
            to="/documents/work-instructions"
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Work instructions
          </Link>
        </div>
      </header>

      {uploadOpen ? (
        <DocumentModal
          title={isDraftRevisionUpload ? "Upload draft revision" : "Add controlled document"}
          description={
            isDraftRevisionUpload
              ? "Add a new work in progress revision for the open document. Title, document type, targets, and description are inherited from the current revision."
              : "Upload one or more files to the same part number or Product Collection. Multiple files always start as work in progress. For a single replacement draft under an engineering change order, choose one file and attach the change order below."
          }
          onClose={() => {
            setUploadOpen(false);
            setUploadPreset("full");
            setDraftRevisionSource(null);
            setUploadValidationError(null);
          }}
          showCloseButton={false}
        >
          <div className="space-y-4">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle} layout="block">
            <fieldset
              disabled={!hubWritesAllowed || uploadMutation.isPending}
              className="min-w-0 space-y-4 border-0 p-0"
            >
            {isDraftRevisionUpload ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <div className="font-medium text-slate-900">{draftRevisionSource.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span>{draftRevisionSource.document_kind_label}</span>
                  {controlledDocumentTargetsAsPills(draftRevisionSource)}
                </div>
              </div>
            ) : null}
            <div className={`grid gap-4 ${isDraftRevisionUpload ? "max-w-xl" : "lg:grid-cols-3"}`}>
              <label className="block text-sm font-medium text-slate-700">
                File
                <input
                  data-documents-file-input="true"
                  type="file"
                  multiple={!isDraftRevisionUpload}
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:border-slate-200 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-50"
                  disabled={uploadMutation.isPending}
                  onChange={(event) => {
                    const nextFiles = Array.from(event.target.files ?? []);
                    setFiles(nextFiles);
                    setUploadStatuses(nextFiles.map((file) => ({ name: file.name, status: "pending" })));
                    setUploadValidationError(null);
                    if (nextFiles.length > 1) setChangeOrderId("");
                  }}
                />
                {files.length > 0 ? (
                  <span className="mt-1 block text-xs text-slate-500">
                    {files.length} file{files.length === 1 ? "" : "s"} selected
                  </span>
                ) : null}
              </label>
              {!isDraftRevisionUpload ? (
              <label className="block text-sm font-medium text-slate-700">
                Title
                <Input
                  className="mt-1"
                  value={title}
                  disabled={files.length > 1}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={files.length > 1 ? "Bulk uploads use filenames as titles" : undefined}
                />
              </label>
              ) : null}
              <label className="block text-sm font-medium text-slate-700">
                Revision
                <Input className="mt-1" value={revision} onChange={(event) => setRevision(event.target.value)} />
              </label>
              {!isDraftRevisionUpload ? (
              <>
              <label className="block text-sm font-medium text-slate-700">
                Document type
                {files.length > 1 ? (
                  <span className="mt-1 block text-xs font-normal text-slate-500">
                    Bulk uploads leave document type unset so you can set it per revision after upload.
                  </span>
                ) : (
                  <SearchableSelect
                    className="mt-1"
                    value={documentKindCode}
                    onValueChange={(option) => {
                      setDocumentKindCode(option?.value ?? "");
                      setDocumentKindUploadSearch("");
                    }}
                    options={documentKindFieldOptions}
                    searchValue={documentKindUploadSearch}
                    onSearchChange={setDocumentKindUploadSearch}
                    placeholder="Not set"
                    searchPlaceholder="Search document types"
                    emptyMessage="No matching document types."
                    isLoading={lookupsQuery.isLoading}
                  />
                )}
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Target
                <select
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  value={targetType}
                  onChange={(event) => setTargetType(event.target.value as "sku" | "collection")}
                >
                  <option value="sku">Part number / SKU</option>
                  <option value="collection">Product collection</option>
                </select>
              </label>
              {targetType === "sku" ? (
                <label className="block text-sm font-medium text-slate-700">
                  Part number / SKU
                  <SkuSearchableSelect
                    className="mt-1"
                    value={selectedSku}
                    onValueChange={(option) => setSelectedSku(option?.value ?? "")}
                    options={skuOptions}
                    searchValue={skuQuery}
                    onSearchChange={setSkuQuery}
                    placeholder="Choose part number / SKU"
                    searchPlaceholder="Search public.sku"
                    emptyMessage={skuQuery.trim() ? "No SKUs found." : "Type to search SKUs."}
                    isLoading={skuQueryResult.isFetching}
                    selectedLabelOverride={selectedSku}
                  />
                </label>
              ) : (
                <label className="block text-sm font-medium text-slate-700">
                  Product collection
                  <select
                    className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                    value={selectedCollectionId}
                    onChange={(event) => setSelectedCollectionId(event.target.value)}
                  >
                    <option value="">Choose collection</option>
                    {collections.map((collection) => (
                      <option key={collection.id} value={collection.id}>
                        {collection.code} - {collection.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="block text-sm font-medium text-slate-700">
                Engineering change order
                {files.length > 1 ? (
                  <span className="mt-1 block text-xs font-normal text-slate-500">
                    Bulk uploads always start as work in progress. Attach drafts under an engineering change order using a
                    single-file upload only.
                  </span>
                ) : (
                  <select
                    className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                    value={changeOrderId}
                    disabled={uploadMutation.isPending}
                    onChange={(event) => setChangeOrderId(event.target.value)}
                  >
                    <option value="">None — work in progress upload</option>
                    {activeChangeOrders.map((changeOrder) => (
                      <option key={changeOrder.id} value={changeOrder.id}>
                        {formatEcoNumber(changeOrder.number)} - {changeOrder.title}
                      </option>
                    ))}
                  </select>
                )}
              </label>
              <label className="block text-sm font-medium text-slate-700 lg:col-span-3">
                Description
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                  rows={2}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </label>
              </>
              ) : null}
              <label
                className={`block text-sm font-medium text-slate-700 ${isDraftRevisionUpload ? "" : "lg:col-span-3"}`}
              >
                Revision change notes
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                  rows={2}
                  value={changeNotes}
                  onChange={(event) => setChangeNotes(event.target.value)}
                  placeholder="What changed in this revision only"
                />
              </label>
            </div>
            {uploadValidationError ? (
              <p className="text-sm text-red-600">{uploadValidationError}</p>
            ) : null}
            {uploadTotal > 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="font-medium text-slate-900">
                    {uploadMutation.isPending
                      ? `Uploading ${currentUploadOrdinal} of ${uploadTotal}`
                      : failedCount > 0
                        ? `${failedCount} upload failed`
                        : uploadedCount > 0
                          ? `${uploadedCount} of ${uploadTotal} uploaded`
                          : `${uploadTotal} file${uploadTotal === 1 ? "" : "s"} ready`}
                  </span>
                  <span className="text-slate-500">{uploadPercent}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full bg-brand-600 transition-all" style={{ width: `${uploadPercent}%` }} />
                </div>
                {uploadMutation.isPending || failedCount > 0 ? (
                  <ul className="mt-3 max-h-32 space-y-1 overflow-auto text-xs text-slate-600">
                    {uploadStatuses.map((item, index) => (
                      <li key={`${item.name}-${index}`} className="flex items-center justify-between gap-3">
                        <span className="truncate">{item.name}</span>
                        <span
                          className={
                            item.status === "failed"
                              ? "font-medium text-red-600"
                              : item.status === "uploaded"
                                ? "font-medium text-green-700"
                                : item.status === "uploading"
                                  ? "font-medium text-brand-700"
                                  : "text-slate-500"
                          }
                        >
                          {item.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            </fieldset>
            </ReadOnlyHoverLayer>
            <div className="flex justify-end gap-2">
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={uploadMutation.isPending}
                    onClick={() => {
                      setUploadOpen(false);
                      setUploadPreset("full");
                      setDraftRevisionSource(null);
                      setUploadValidationError(null);
                    }}
                  >
                    Cancel
                  </Button>
                }
              >
                Close the upload dialog without finishing uploads in progress.
              </HoverHintTrigger>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    variant="primary"
                    isLoading={uploadMutation.isPending}
                    disabled={files.length === 0 || uploadMutation.isPending || !hubWritesAllowed}
                    onClick={handleUploadClick}
                  >
                    {uploadMutation.isPending
                      ? `Uploading ${currentUploadOrdinal} of ${uploadTotal}`
                      : isDraftRevisionUpload
                        ? "Upload revision"
                        : `Upload ${files.length > 1 ? `${files.length} documents` : "document"}`}
                  </Button>
                }
              >
                {isDraftRevisionUpload
                  ? "Upload the new revision file; title, type, and targets stay the same as the open document."
                  : "Send the selected files to the server as controlled documents for the chosen target and metadata."}
              </HoverHintTrigger>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </DocumentModal>
      ) : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <aside className="w-full shrink-0 lg:sticky lg:top-4 lg:w-72 lg:self-start xl:w-80">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="flex flex-col gap-3">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600">Search documents, ECNs, notes</span>
                  <Input
                    className="mt-1"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="@sku ext or sku:ext — part numbers only"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Use <span className="font-mono">@sku</span> or <span className="font-mono">sku:</span> to search
                    part numbers only; without a scope, title, filename, ECN, owner name, and targets are included.
                    Unscoped search starts after two characters.
                  </p>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600">Status</span>
                  <select
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="wip">WIP</option>
                    <option value="draft">Draft</option>
                    <option value="current">Released</option>
                    <option value="obsolete">Obsolete</option>
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600">Document type</span>
                  <SearchableSelect
                    className="mt-1"
                    triggerClassName="h-9 text-sm"
                    value={documentKindFilter}
                    onValueChange={(option) => {
                      setDocumentKindFilter(option?.value ?? "");
                      setDocumentKindFilterSearch("");
                    }}
                    options={documentKindFilterSelectOptions}
                    searchValue={documentKindFilterSearch}
                    onSearchChange={setDocumentKindFilterSearch}
                    placeholder="All types"
                    searchPlaceholder="Search document types"
                    emptyMessage="No matching document types."
                    isLoading={lookupsQuery.isLoading}
                  />
                </label>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Scope</span>
                <div className="mt-3 space-y-3">
                  <HoverHintTrigger
                    className="block w-full"
                    trigger={
                      <button
                        type="button"
                        className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium ${
                          selection.type === "all" ? "bg-brand-50 text-brand-700" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => setSelection({ type: "all" })}
                      >
                        All document control
                      </button>
                    }
                  >
                    Show every controlled document across collections and part numbers.
                  </HoverHintTrigger>
                  <HoverHintTrigger
                    className="block w-full"
                    trigger={
                      <button
                        type="button"
                        className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium ${
                          selection.type === "wip" ? "bg-brand-50 text-brand-700" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => setSelection({ type: "wip" })}
                      >
                        WIP files
                      </button>
                    }
                  >
                    Show files attached to a person rather than a part number or collection. Anyone with Document Control
                    access can see them.
                  </HoverHintTrigger>
                  <div>
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Collections</h2>
                    <div className="mt-2 space-y-1">
                      {collections.map((collection) => (
                        <HoverHintTrigger
                          key={collection.id}
                          className="block w-full"
                          trigger={
                            <button
                              type="button"
                              className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                                selection.type === "collection" && selection.id === collection.id
                                  ? "bg-brand-50 font-medium text-brand-700"
                                  : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                              }`}
                              onClick={() => setSelection({ type: "collection", id: collection.id })}
                            >
                              <span className="block">{collection.name}</span>
                              <span className="text-xs text-slate-500">{collection.code}</span>
                            </button>
                          }
                        >
                          Filter documents to those applicable to this product collection.
                        </HoverHintTrigger>
                      ))}
                    </div>
                  </div>
                  {selectedCollection ? (
                    <div>
                      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Member part numbers</h2>
                      <div className="mt-2 space-y-1">
                        {(collectionSkusQuery.data?.skus ?? []).length === 0 ? (
                          <p className="text-xs text-slate-500">No member SKUs yet.</p>
                        ) : (
                          (collectionSkusQuery.data?.skus ?? []).map((sku) => (
                            <HoverHintTrigger
                              key={sku.sku}
                              className="block w-full"
                              trigger={
                                <button
                                  type="button"
                                  className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                                    selection.type === "sku" && selection.sku === sku.sku
                                      ? "bg-brand-50 font-medium text-brand-700"
                                      : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                                  }`}
                                  onClick={() =>
                                    setSelection({ type: "sku", sku: sku.sku, collectionId: selectedCollection.id })
                                  }
                                >
                                  <span className="block font-mono text-xs">{sku.sku}</span>
                                  {sku.product_name ? <span className="text-xs text-slate-500">{sku.product_name}</span> : null}
                                </button>
                              }
                            >
                              Filter documents to this member part number within the selected collection.
                            </HoverHintTrigger>
                          ))
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </CardBody>
          </Card>
        </aside>

        <div className="min-w-0 flex-1 space-y-4">
              {selection.type === "collection" ? (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-md bg-slate-50 p-3">
                      <h3 className="text-sm font-semibold text-slate-900">Related collections</h3>
                      {(relationshipsQuery.data?.relationships ?? []).length === 0 ? (
                        <p className="mt-2 text-sm text-slate-500">No relationships recorded.</p>
                      ) : (
                        <ul className="mt-2 space-y-1 text-sm text-slate-700">
                          {(relationshipsQuery.data?.relationships ?? []).map((relationship) => (
                            <li key={relationship.id}>
                              {relationship.from_collection_name} {relationship.relation_type.replaceAll("_", " ")}{" "}
                              {relationship.to_collection_name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <h3 className="text-sm font-semibold text-slate-900">Active transition notices</h3>
                      {activeTransitionNotices.length === 0 ? (
                        <p className="mt-2 text-sm text-slate-500">No active transition notices.</p>
                      ) : (
                        <ul className="mt-2 space-y-2 text-sm">
                          {activeTransitionNotices.map((notice) => (
                            <li key={notice.id}>
                              <span
                                className={`rounded-full px-2 py-1 text-xs font-medium ${transitionStatusClass(notice.status)}`}
                              >
                                {notice.status}
                              </span>{" "}
                              {notice.notice_number} - {notice.title}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {openChangeOrders.length > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <h3 className="text-sm font-semibold text-amber-900">Open ECOs touching this context</h3>
                  <ul className="mt-2 space-y-1 text-sm text-amber-900">
                    {openChangeOrders.map((changeOrder) => (
                      <li key={changeOrder.id}>
                        {formatEcoNumber(changeOrder.number)} - {changeOrder.title} ({changeOrder.status})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {notesQuery.data?.notes?.length ? (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <h3 className="text-sm font-semibold text-slate-900">Notes</h3>
                  <ul className="mt-2 space-y-2">
                    {notesQuery.data.notes.slice(0, 3).map((note) => (
                      <li key={note.id} className="text-sm text-slate-700">
                        {note.body}
                        <span className="ml-2 text-xs text-slate-500">{formatTimestamp(note.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {documentsQuery.isError ? (
                <Card>
                  <CardBody>
                    <p className="text-sm text-red-600">{documentsQuery.error.message}</p>
                  </CardBody>
                </Card>
              ) : documentsQuery.isLoading ? (
                <Card>
                  <CardBody>
                    <p className="text-sm text-slate-600">Loading documents…</p>
                  </CardBody>
                </Card>
              ) : sortedDocumentGroups.length === 0 ? (
                <Card>
                  <CardBody>
                    <p className="text-sm text-slate-500">No documents match this context.</p>
                  </CardBody>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Documents</CardTitle>
                  </CardHeader>
                  <CardBody className="overflow-x-auto p-0">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        <th scope="col" className="px-3 py-2">
                          <HoverHintTrigger
                            trigger={
                              <button
                                type="button"
                                className="-mx-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={() => toggleDocumentSort("title")}
                              >
                                Title
                                {documentSort.key === "title" ? (documentSort.dir === "asc" ? " ↑" : " ↓") : ""}
                              </button>
                            }
                          >
                            Sort the table by document title; click again to reverse order.
                          </HoverHintTrigger>
                        </th>
                        <th scope="col" className="px-3 py-2">
                          <HoverHintTrigger
                            trigger={
                              <button
                                type="button"
                                className="-mx-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={() => toggleDocumentSort("document_kind_label")}
                              >
                                Document type
                                {documentSort.key === "document_kind_label"
                                  ? documentSort.dir === "asc"
                                    ? " ↑"
                                    : " ↓"
                                  : ""}
                              </button>
                            }
                          >
                            Sort by document type label.
                          </HoverHintTrigger>
                        </th>
                        <th scope="col" className="px-3 py-2">
                          <HoverHintTrigger
                            trigger={
                              <button
                                type="button"
                                className="-mx-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={() => toggleDocumentSort("extension")}
                              >
                                File type
                                {documentSort.key === "extension" ? (documentSort.dir === "asc" ? " ↑" : " ↓") : ""}
                              </button>
                            }
                          >
                            Sort by file extension from the original filename.
                          </HoverHintTrigger>
                        </th>
                        <th scope="col" className="px-3 py-2">
                          <HoverHintTrigger
                            trigger={
                              <button
                                type="button"
                                className="-mx-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={() => toggleDocumentSort("revision")}
                              >
                                Active rev
                                {documentSort.key === "revision" ? (documentSort.dir === "asc" ? " ↑" : " ↓") : ""}
                              </button>
                            }
                          >
                            Sort by the released revision letter for this document, if any.
                          </HoverHintTrigger>
                        </th>
                        <th scope="col" className="px-3 py-2">
                          <HoverHintTrigger
                            trigger={
                              <button
                                type="button"
                                className="-mx-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                onClick={() => toggleDocumentSort("lifecycle_status")}
                              >
                                Status
                                {documentSort.key === "lifecycle_status"
                                  ? documentSort.dir === "asc"
                                    ? " ↑"
                                    : " ↓"
                                  : ""}
                              </button>
                            }
                          >
                            Sort by lifecycle status (draft, work in progress, released, obsolete).
                          </HoverHintTrigger>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {sortedDocumentGroups.map((group) => {
                        const document = group.representative;
                        const listStatus = controlledDocumentListStatusSummary(group.revisions);
                        const listActiveRevision = controlledDocumentListActiveRevisionLabel(group.revisions);
                        const canDownload = controlledDocumentIsDownloadable(document);
                        return (
                          <tr
                            key={group.key}
                            role="button"
                            tabIndex={0}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => openDocumentDetails(document)}
                            onMouseEnter={() => prefetchDocumentPreview(document)}
                            onFocus={() => prefetchDocumentPreview(document)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openDocumentDetails(document);
                              }
                            }}
                          >
                            <td className="max-w-lg px-3 py-3 align-top">
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <span className="font-medium text-slate-900">{document.title}</span>
                                <HoverHintTrigger
                                  trigger={
                                    <button
                                      type="button"
                                      className="inline-flex shrink-0 rounded-md p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
                                      aria-label={`Download ${document.title}`}
                                      disabled={workbenchBusy || !canDownload}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (!canDownload) return;
                                        void downloadControlledDocument(document);
                                      }}
                                    >
                                      <Download className="h-4 w-4" aria-hidden />
                                    </button>
                                  }
                                >
                                  {canDownload
                                    ? "Download this document file without opening the detail panel."
                                    : "Download needs Document Control administrator access or a share."}
                                </HoverHintTrigger>
                                {controlledDocumentTargetsAsPills(document, {
                                  linkCollections: true,
                                  linkSkus: true,
                                  stopPropagation: true,
                                })}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 align-top text-slate-700">{document.document_kind_label}</td>
                            <td className="whitespace-nowrap px-3 py-3 align-top tabular-nums text-slate-700">
                              {filenameExtension(document.original_filename) ?? "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 align-top text-slate-600">{listActiveRevision}</td>
                            <td className="whitespace-nowrap px-3 py-3 align-top">
                              <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(listStatus)}`}>
                                {statusChipLabel(listStatus)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </CardBody>
                </Card>
              )}
        </div>
      </div>

      {selectedDocument ? (
        <ControlledDocumentDetailModal
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
          skuContext={primarySkuFromControlledDocument(selectedDocument)}
          layout="workbench"
          downloadDisabled={workbenchBusy || !controlledDocumentIsDownloadable(selectedDocument)}
          documentsReadOnlyTitle={documentsReadOnlyTitle}
          inboundEmailThread={documentHistoryQuery.data?.inboundEmailThread ?? null}
          previewMode={detailPreviewMode}
          onPreviewModeChange={setDetailPreviewMode}
        >
            {selectedDocument.lifecycle_status === "current" ? (
              <div className="space-y-3">
                <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                  This revision is released. Stage a replacement revision under an engineering change order to change the
                  controlled file.
                </p>
                <div className="flex flex-wrap gap-2">
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                  <HoverHintTrigger
                    trigger={
                      <Button
                        type="button"
                        variant="primary"
                        disabled={workbenchBusy || !hubWritesAllowed}
                        onClick={() => openUploadFromControlledDocument(selectedDocument, "replacement")}
                      >
                        Stage replacement file
                      </Button>
                    }
                  >
                    Open upload with this document selected so you can attach a new revision under an engineering change order.
                  </HoverHintTrigger>
                  </ReadOnlyHoverLayer>
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                  <HoverHintTrigger
                    trigger={
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={workbenchBusy || !hubWritesAllowed}
                        onClick={() => openUploadFromControlledDocument(selectedDocument, "draft_revision")}
                      >
                        Upload a draft revision
                      </Button>
                    }
                  >
                    Close this panel and open upload with the same title and targets. Leave engineering change order unset for a
                    work in progress row, or pick one after you create it. Set a new revision if it would collide with an existing
                    row.
                  </HoverHintTrigger>
                  </ReadOnlyHoverLayer>
                  {hubWritesAllowed ? (
                    <HoverHintTrigger
                      trigger={
                        <Link to={`/documents/change-orders?create=1&documentId=${encodeURIComponent(selectedDocument.id)}`}>
                          <Button type="button" variant="secondary">
                            Create ECO
                          </Button>
                        </Link>
                      }
                    >
                      Go to the change queue with the new engineering change order form open.
                    </HoverHintTrigger>
                  ) : (
                    <ReadOnlyHoverLayer active hoverHint={documentsReadOnlyTitle}>
                      <HoverHintTrigger
                        trigger={<Button type="button" variant="secondary" disabled>Create ECO</Button>}
                      >
                        Go to the change queue with the new engineering change order form open.
                      </HoverHintTrigger>
                    </ReadOnlyHoverLayer>
                  )}
                  <HoverHintTrigger
                    trigger={
                      <Link to="/documents/change-orders">
                        <Button type="button" variant="secondary">
                          Change orders
                        </Button>
                      </Link>
                    }
                  >
                    Open the engineering change order queue to manage approvals and implementation.
                  </HoverHintTrigger>
                </div>
                <p className="text-xs text-slate-500">
                  One engineering change order can include several replacement documents; they become released together when you
                  implement the change order.
                </p>
              </div>
            ) : (
              <>
                {selectedDocument.lifecycle_status === "wip" ? (
                  <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                    {isPersonOwnedWipDocument(selectedDocument)
                      ? "This WIP file is attached to a person, not a part number. Identification notes from the inbound email are in Description. Use Email in the preview pane to read the full thread. Attach it to a product or collection when you know where it belongs."
                      : "This revision is work in progress. Edit metadata below, then mark it as released when it is ready, or use an engineering change order if replacements must track approvals separately."}
                  </p>
                ) : null}
                <section className="rounded-lg border border-slate-200 p-3">
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle} layout="block">
                  <fieldset
                    disabled={!hubWritesAllowed}
                    className="border-0 p-0"
                  >
                  <div className="space-y-5">
                    <div>
                      <CardTitleWithHelp
                        hoverHint="The document line groups every revision that shares this title, document type, and applicability targets. Title and document type should stay aligned across revisions."
                        titleClassName="text-sm font-semibold text-slate-900"
                      >
                        Controlled document
                      </CardTitleWithHelp>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Title
                      <Input className="mt-1" value={editTitle} onChange={(event) => setEditTitle(event.target.value)} />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Document type
                      <SearchableSelect
                        className="mt-1"
                        value={editDocumentKindCode}
                        onValueChange={(option) => {
                          setEditDocumentKindCode(option?.value ?? "");
                          setEditDocumentKindSearch("");
                        }}
                        options={documentKindFieldOptions}
                        searchValue={editDocumentKindSearch}
                        onSearchChange={setEditDocumentKindSearch}
                        placeholder="Not set"
                        searchPlaceholder="Search document types"
                        emptyMessage="No matching document types."
                        isLoading={lookupsQuery.isLoading}
                        selectedLabelOverride={
                          editDocumentKindCode.trim() !== "" &&
                          !documentKindFieldOptions.some((option) => option.value === editDocumentKindCode.trim()) &&
                          selectedDocument
                            ? selectedDocument.document_kind_label
                            : null
                        }
                      />
                    </label>
                      </div>
                    </div>
                    <div>
                      <CardTitleWithHelp
                        hoverHint="File, revision letter, and description belong to this revision row only. Revision change notes are edited from revision history (hover a row for notes; right-click a work in progress row to edit)."
                        titleClassName="text-sm font-semibold text-slate-900"
                      >
                        {selectedDocument.lifecycle_status === "draft"
                          ? "Draft revision"
                          : `Revision ${editRevision.trim() || selectedDocument.revision}`}
                      </CardTitleWithHelp>
                      <div className="mt-3 grid gap-3">
                    <label className="block text-sm font-medium text-slate-700 sm:max-w-xs">
                      Revision
                      <Input className="mt-1" value={editRevision} onChange={(event) => setEditRevision(event.target.value)} />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Description
                      <textarea
                        className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                        rows={isPersonOwnedWipDocument(selectedDocument) ? 6 : 2}
                        value={editDescription}
                        onChange={(event) => setEditDescription(event.target.value)}
                      />
                    </label>
                      </div>
                    </div>
                  </div>
                  </fieldset>
                  </ReadOnlyHoverLayer>
                  {updateDocumentMutation.isError ? (
                    <p className="mt-3 text-sm text-red-600">{(updateDocumentMutation.error as Error).message}</p>
                  ) : null}
                  {releaseWipMutation.isError ? (
                    <p className="mt-3 text-sm text-red-600">{(releaseWipMutation.error as Error).message}</p>
                  ) : null}
                  {deleteRevisionMutation.isError ? (
                    <p className="mt-3 text-sm text-red-600">{(deleteRevisionMutation.error as Error).message}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                      <HoverHintTrigger
                        trigger={
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={workbenchBusy || !hubWritesAllowed}
                            onClick={() => openUploadFromControlledDocument(selectedDocument, "draft_revision")}
                          >
                            Upload a draft revision
                          </Button>
                        }
                      >
                        Close this panel and open upload with the same title and targets. Leave engineering change order unset for
                        a work in progress row, or pick one after you create it. Set a new revision if it would collide with an
                        existing row.
                      </HoverHintTrigger>
                      </ReadOnlyHoverLayer>
                      {hubWritesAllowed ? (
                        <HoverHintTrigger
                          trigger={
                            <Link
                              to={`/documents/change-orders?create=1&documentId=${encodeURIComponent(selectedDocument.id)}`}
                            >
                              <Button type="button" variant="secondary">
                                Create ECO
                              </Button>
                            </Link>
                          }
                        >
                          Go to the change queue with the new engineering change order form open.
                        </HoverHintTrigger>
                      ) : (
                        <ReadOnlyHoverLayer active hoverHint={documentsReadOnlyTitle}>
                          <HoverHintTrigger
                            trigger={<Button type="button" variant="secondary" disabled>Create ECO</Button>}
                          >
                            Go to the change queue with the new engineering change order form open.
                          </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      )}
                      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              variant="danger"
                              disabled={workbenchBusy || !hubWritesAllowed || deleteRevisionMutation.isPending}
                              onClick={() => requestDeleteRevision(selectedDocument)}
                            >
                              Delete this revision
                            </Button>
                          }
                        >
                          Remove this work in progress or draft revision and its file from storage so you can upload again.
                        </HoverHintTrigger>
                      </ReadOnlyHoverLayer>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      {selectedDocument.lifecycle_status === "wip" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={
                                workbenchBusy ||
                                releaseWipMutation.isPending ||
                                editTitle.trim() === "" ||
                                editRevision.trim() === "" ||
                                !hubWritesAllowed
                              }
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    "Mark this revision as released? Other released or work in progress revisions with the same title, document type, and applicability will be marked obsolete.",
                                  )
                                ) {
                                  return;
                                }
                                releaseWipMutation.mutate();
                              }}
                            >
                              Mark as released
                            </Button>
                          }
                        >
                          Promote this work in progress revision to released and obsolete conflicting revisions after confirmation.
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                      <HoverHintTrigger
                        trigger={
                          <Button
                            type="button"
                            variant="primary"
                            disabled={
                              updateDocumentMutation.isPending ||
                              editTitle.trim() === "" ||
                              editRevision.trim() === "" ||
                              !hubWritesAllowed
                            }
                            onClick={() => updateDocumentMutation.mutate()}
                          >
                            Save revision metadata
                          </Button>
                        }
                      >
                        Save title, document type, revision, and description for this draft or work in progress row.
                      </HoverHintTrigger>
                      </ReadOnlyHoverLayer>
                    </div>
                  </div>
                </section>
              </>
            )}
            <div className="grid gap-4 lg:grid-cols-3">
              {documentHistoryQuery.isPending ? (
                <p className="text-sm text-slate-500 lg:col-span-3">Loading revision history and audit events…</p>
              ) : null}
            <section className="rounded-lg border border-slate-200 p-3 lg:col-span-1">
              <h2 className="text-sm font-semibold text-slate-900">Revision history</h2>
              {!documentHistoryQuery.isPending ? (
                <ul className="mt-2 space-y-2 text-sm">
                  {(documentHistoryQuery.data?.revisions ?? []).map((revision) => (
                    <li
                      key={revision.id}
                      className="rounded-md bg-slate-50 p-2"
                      onContextMenu={(event) => {
                        if (revision.lifecycle_status !== "wip" || !hubWritesAllowed) return;
                        event.preventDefault();
                        setRevisionContextMenu({
                          revision,
                          clientX: event.clientX,
                          clientY: event.clientY,
                        });
                      }}
                    >
                      <HoverHintContent hoverHint={revisionChangeNotesHoverHint(revision)} block className="min-w-0">
                        <div className="font-medium text-slate-900">
                          Rev {revision.revision} - {revision.lifecycle_status}
                        </div>
                        <div className="truncate text-xs text-slate-500">{revision.original_filename}</div>
                        <div className="text-xs text-slate-500">
                          {revision.change_order_number ? (
                            <>
                              Engineering change order {revision.change_order_number} ·{" "}
                              {formatTimestamp(revision.updated_at)}
                            </>
                          ) : (
                            formatTimestamp(revision.updated_at)
                          )}
                        </div>
                      </HoverHintContent>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
            <section className="rounded-lg border border-slate-200 p-3 lg:col-span-2">
              <h2 className="text-sm font-semibold text-slate-900">Audit timeline</h2>
              {!documentHistoryQuery.isPending ? (
                <ul className="mt-2 space-y-2 text-sm">
                  {(documentHistoryQuery.data?.events ?? []).map((event) => {
                    const inboundEmailThread = documentHistoryQuery.data?.inboundEmailThread ?? null;
                    const showInboundEmailHint =
                      inboundEmailThread != null && isInboundEmailCreatedEvent(event.event_note);
                    const eventBody = (
                      <>
                        <div className="font-medium text-slate-900">{event.event_type}</div>
                        <div className="text-xs text-slate-500">
                          {revisionEventActorLabel(event)} · {formatTimestamp(event.created_at)}
                        </div>
                        {event.event_note ? <div className="mt-1 text-slate-600">{event.event_note}</div> : null}
                        {showInboundEmailHint ? (
                          <button
                            type="button"
                            className="mt-1 text-xs font-medium text-blue-700 hover:underline"
                            onClick={() => setDetailPreviewMode("email")}
                          >
                            Show email thread in preview
                          </button>
                        ) : null}
                      </>
                    );
                    return (
                      <li key={event.id} className="rounded-md bg-slate-50 p-2">
                        {showInboundEmailHint ? (
                          <HoverHintContent
                            hoverHint={documentInboundEmailThreadHoverHint(inboundEmailThread)}
                            block
                            className="min-w-0"
                            cursor="default"
                          >
                            {eventBody}
                          </HoverHintContent>
                        ) : (
                          eventBody
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
            </div>
        </ControlledDocumentDetailModal>
      ) : null}

      {revisionContextMenu ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[45] cursor-default bg-transparent"
            aria-label="Close revision menu"
            onClick={() => setRevisionContextMenu(null)}
          />
          <div
            className="fixed z-50 min-w-[12rem] rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
            style={{ left: revisionContextMenu.clientX, top: revisionContextMenu.clientY }}
            role="menu"
          >
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-slate-800 hover:bg-slate-50"
              role="menuitem"
              onClick={() => openRevisionChangeNotesEditor(revisionContextMenu.revision)}
            >
              Edit change notes
            </button>
          </div>
        </>
      ) : null}

      {revisionChangeNotesEdit ? (
        <DocumentModal
          title={`Edit change notes — Rev ${revisionChangeNotesEdit.revision}`}
          onClose={() => setRevisionChangeNotesEdit(null)}
          shellClassName="max-w-lg"
        >
          <label className="block text-sm font-medium text-slate-700">
            Revision change notes
            <textarea
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
              rows={5}
              value={revisionChangeNotesDraft}
              onChange={(event) => setRevisionChangeNotesDraft(event.target.value)}
              placeholder="What changed in this revision only"
            />
          </label>
          {patchRevisionChangeNotesMutation.isError ? (
            <p className="mt-3 text-sm text-red-600">{(patchRevisionChangeNotesMutation.error as Error).message}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setRevisionChangeNotesEdit(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={patchRevisionChangeNotesMutation.isPending}
              onClick={() =>
                patchRevisionChangeNotesMutation.mutate({
                  documentId: revisionChangeNotesEdit.id,
                  changeNotes: revisionChangeNotesDraft,
                })
              }
            >
              Save
            </Button>
          </div>
        </DocumentModal>
      ) : null}
    </div>
  );
}
